import { useState, useEffect, useContext, useRef } from "react";
import { Grid, Typography, useMediaQuery } from "@mui/material";
import axios from "axios";
import BASE_URL from "../../../../constants/baseUrls.js";
import { toast } from "react-toastify";
import { t } from "i18next";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { LocaleContext } from "../../../../routing/LangRouter.js";
import { useForm } from "react-hook-form";
import { numberToText } from "../../../../utils/numberToText.js";
import { maskCardNumber } from "../../../../utils/utility.js";

// General components
import { BaseButton } from "../../../common/base-button/base-button.jsx";
import BaseTextField from "../../../common/base-text-field/base-text-field.jsx";
import AutoCompleteComponent from "../../../common/base-select/autocomplete-component.js.jsx";

// icons
import { AddCircle } from "iconsax-react";

// Styles
import "./deposit.scss";

// Helper function to map locale to supported languages
const getLanguageFromLocale = locale => {
    const languageMap = {
        fa: "persian",
        en: "english",
        ar: "arabic",
    };

    return languageMap[locale] || "persian";
};

function DepositIncrement() {
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const [cardOptions, setCardOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCard, setSelectedCard] = useState("");
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [state, setState] = useState({
        amount: "",
    });
    const [cardError, setCardError] = useState(false);
    const hasFetchedCards = useRef(false);
    const [amountError, setAmountError] = useState(false);
    const { control } = useForm();

    // Get the current language for number conversion
    const currentLanguage = getLanguageFromLocale(locale);

    const fetchCards = async () => {
        if (hasFetchedCards.current) return; // Avoid multiple calls
        hasFetchedCards.current = true; // Mark as fetched

        try {
            const accessToken = localStorage.getItem("accessToken");

            const response = await axios.get(`${BASE_URL}/account/user/bank_account/list/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`, // Add auth if required
                },
            });

            const cards = Array.isArray(response.data.results) ? response.data.results : [];

            const formattedCards = cards.map(card => ({
                ...card,
                name: maskCardNumber(card.card_number),
                label: maskCardNumber(card.card_number),
                image: `https://api.atripa.com${card.bank.logo}`,
            }));

            setCardOptions(formattedCards);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching card options:", error);
            setCardOptions([]);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCards(); // Fetch once on component mount

        const handleFocus = () => {
            fetchCards(); // Ensure we fetch again when window regains focus
        };

        // Add event listener for when the window regains focus
        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, []);

    const handleCardChange = selectedValue => {
        if (selectedValue?.id === "add-new-card") {
            navigate({
                pathname: `/${locale}/user-panel/user-account`,
                search: `?${createSearchParams({ prevLocation: location.pathname, tab: "bankAccountInformation" })}`,
            });
        } else if (selectedValue) {
            setSelectedCard(selectedValue.card_number || "");
            setSelectedCardId(selectedValue.id);
            setCardError(false);
        } else {
            // Handle case when selection is cleared
            setSelectedCard("");
            setSelectedCardId(null);
        }
    };

    // 2. Create a function to find the selected card object for defaultValue
    const getSelectedCardObject = () => {
        if (!selectedCard) return null;

        const allOptions = [
            ...cardOptions,
            {
                id: "add-new-card",
                name: t("addNewCard"),
                label: t("addNewCard"),
                image: null,
            },
        ];

        return allOptions.find(option => option.card_number === selectedCard) || null;
    };

    const onUpdateField = e => {
        const { name, value } = e.target;
        const rawValue = value.replace(/,/g, ""); // Remove existing commas

        if (rawValue === "" || /^\d+$/.test(rawValue)) {
            setState(values => ({ ...values, [name]: rawValue }));

            // Validate the amount
            if (rawValue && parseInt(rawValue) < 100000) {
                setAmountError(true); // Set error if amount is less than 100,000
            } else {
                setAmountError(false); // Clear error if amount is valid
            }

            // Update the formatted display value and multilingual text
            if (rawValue) {
                setDisplayValue(formatNumber(rawValue));
                // Use the new multilingual function with current language
                setNumberText(numberToText(parseInt(rawValue), currentLanguage));
            } else {
                setDisplayValue("");
                setNumberText("");
            }
        }
    };

    const registerReceipt = async event => {
        event.preventDefault();
        setLoading(true);

        const accessToken = localStorage.getItem("accessToken");

        const params = {
            amount: state.amount,
            card_id: selectedCardId,
        };

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            };

            const response = await axios.get(`${BASE_URL}/api/v2/account/user/charge-account/`, {
                headers: headers,
                params,
            });
            if (response.status === 200) {
                const opgLink = response.data.data.opg;
                window.open(opgLink, "_blank");
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.clear();
                navigate({
                    pathname: `/${locale}/authentication/signin`,
                    search: `?${createSearchParams({ prevLocation: location.pathname })}`,
                });
                toast.error(t("SearchExpired"));
            } else {
                toast.error(t("Toast Messages.Error Occurred"));
            }
        } finally {
            setLoading(false);
        }
    };

    const [displayValue, setDisplayValue] = useState("");
    // Renamed from persianText to numberText to reflect multilingual support
    const [numberText, setNumberText] = useState("");

    // Format number with commas
    const formatNumber = num => {
        return new Intl.NumberFormat("en-US").format(num);
    };

    useEffect(() => {
        if (state.amount) {
            setDisplayValue(formatNumber(state.amount));
            // Use the new multilingual function
            setNumberText(numberToText(parseInt(state.amount), currentLanguage));
        } else {
            setDisplayValue("");
            setNumberText("");
        }
    }, [state.amount, currentLanguage]);

    return (
        <Grid container item xs={12} justifyContent="center">
            <Grid component="form" container item xs={9} onSubmit={registerReceipt} rowGap={3}>
                <Grid container item xs={12}>
                    <BaseTextField
                        fullWidth
                        variant="outlined"
                        label={t("amount")}
                        name="amount"
                        value={displayValue}
                        setValue={onUpdateField}
                        sx={{ mb: 1 }}
                        inputProps={{
                            inputMode: "numeric",
                        }}
                        radius="10px"
                        error={amountError ? t("refundAmountHelperText") : ""}
                    />
                    {numberText && (
                        <Typography
                            variant="body2"
                            sx={{
                                padding: 1,
                                color: "text.secondary",
                            }}
                        >
                            {numberText}
                        </Typography>
                    )}
                </Grid>
                <AutoCompleteComponent
                    width="auto"
                    label={t("selectCard")}
                    options={[
                        ...cardOptions,
                        {
                            id: "add-new-card",
                            name: t("addNewCard"),
                            label: t("addNewCard"),
                            image: null,
                        },
                    ]}
                    name="card"
                    defaultValue={getSelectedCardObject()}
                    handleChange={handleCardChange}
                    error={cardError}
                    helperText={cardError ? t("pleaseSelectCard") : ""}
                    control={control}
                    loading={loading}
                    showImages={true}
                    imageField="image"
                    imageFallback={<AddCircle size={24} />}
                />
                <BaseButton
                    text={t("submit")}
                    color="primary"
                    size="large"
                    radius="radius-10"
                    type="submit"
                    disabled={!selectedCard || !state.amount || amountError}
                    fullWidth
                />
            </Grid>
        </Grid>
    );
}

export default DepositIncrement;
